import { Check, Loader2, X } from 'lucide-react';
import * as React from 'react';
import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '~/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '~/components/ui/popover';
import { cn } from '~/lib/utils';

export interface MultiSelectOption {
  id: string;
  name: string;
  jpName?: string | null;
  slug?: string;
  avatar?: string | null;
}

interface MultiSelectSearchProps {
  selectedIds: string[];
  selectedItems: MultiSelectOption[];
  onChange: (ids: string[]) => void;
  onSearch: (query: string) => void;
  searchResults: MultiSelectOption[];
  placeholder?: string;
  emptyText?: string;
  isLoading?: boolean;
  className?: string;
}

export function MultiSelectSearch({
  selectedIds,
  selectedItems,
  onChange,
  onSearch,
  searchResults,
  placeholder = 'Select...',
  emptyText = 'No results found.',
  isLoading = false,
  className,
}: MultiSelectSearchProps) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');

  const handleSearch = React.useMemo(() => {
    let timeoutId: NodeJS.Timeout;
    return (searchValue: string) => {
      setSearch(searchValue);
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        onSearch(searchValue);
      }, 300);
    };
  }, [onSearch]);

  const toggleItem = (id: string) => {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter((sid) => sid !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  };

  const removeItem = (id: string) => {
    onChange(selectedIds.filter((sid) => sid !== id));
  };

  // Combine selected items with search results, dedupe
  const allOptions = React.useMemo(() => {
    const map = new Map<string, MultiSelectOption>();
    selectedItems.forEach((item) => map.set(item.id, item));
    searchResults.forEach((item) => map.set(item.id, item));
    return Array.from(map.values());
  }, [selectedItems, searchResults]);

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {/* Selected Items */}
      {selectedItems.length > 0 && (
        <div className='flex flex-wrap gap-2'>
          {selectedItems.map((item) => (
            <Badge key={item.id} variant='secondary' className='flex items-center gap-1 pl-2 pr-1'>
              {item.avatar && <img src={item.avatar} alt='' className='w-4 h-4 rounded-full object-cover' />}
              <span className='text-xs'>
                {item.name}
                {item.jpName && <span className='ml-1 text-muted-foreground'>({item.jpName})</span>}
              </span>
              <Button
                variant='ghost'
                size='sm'
                className='h-4 w-4 p-0 hover:bg-transparent'
                onClick={() => removeItem(item.id)}
              >
                <X className='h-3 w-3' />
              </Button>
            </Badge>
          ))}
        </div>
      )}

      {/* Search Popover */}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant='outline' className='w-full justify-start text-left font-normal'>
            {selectedItems.length > 0 ? (
              <span className='text-sm'>{selectedItems.length} selected - Click to add more</span>
            ) : (
              <span className='text-muted-foreground'>{placeholder}</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className='w-[400px] p-0' align='start'>
          <Command shouldFilter={false}>
            <CommandInput
              placeholder={`Search ${placeholder.toLowerCase()}...`}
              value={search}
              onValueChange={handleSearch}
            />
            <CommandList>
              {isLoading ? (
                <div className='flex items-center justify-center py-6'>
                  <Loader2 className='h-4 w-4 animate-spin' />
                  <span className='ml-2 text-sm text-muted-foreground'>Loading...</span>
                </div>
              ) : (
                <>
                  <CommandEmpty>{emptyText}</CommandEmpty>
                  <CommandGroup>
                    {allOptions.map((option) => {
                      const isSelected = selectedIds.includes(option.id);
                      return (
                        <CommandItem key={option.id} value={option.id} onSelect={() => toggleItem(option.id)}>
                          <Check className={cn('mr-2 h-4 w-4', isSelected ? 'opacity-100' : 'opacity-0')} />
                          {option.avatar && (
                            <img src={option.avatar} alt='' className='mr-2 w-6 h-6 rounded-full object-cover' />
                          )}
                          <span className='flex-1 truncate'>
                            {option.name}
                            {option.jpName && (
                              <span className='ml-2 text-xs text-muted-foreground'>({option.jpName})</span>
                            )}
                          </span>
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
